/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

//--------------------- ALERT ------------------------
// Did not implement this with the constructor support, will have to add later if necessary

// TODO
// come up with better write implementation for objects... they don't need to be
// as complex or as disjointed as lists

// This class does not support listing to raw events/ actions and will return only "it updated"
// See [[FancyFirebaseSplitDataObject]] for an example of applying the default actions despite that

import firebase from 'firebase/compat/app';
import {
	AngularFireDatabase,
	AngularFireList,
	AngularFireObject,
	AngularFireAction,
	DatabaseSnapshot,
} from '@angular/fire/compat/database';
import {
	Observable,
	Subject,
	BehaviorSubject,
	ConnectableObservable,
	queueScheduler,
	merge,
	EMPTY,
} from 'rxjs';
import { takeUntil, map, observeOn } from 'rxjs/operators';

import { FancyAction } from './FancyAction';
import { AngularLandConnection, RefOrObservable } from './interfaces';

// import { FancySupplementalAction, SupplementalActionTypes } from 'src/app/app-data/actions/supplemental-actions';

import { IFancyActionTrackerObj } from '../services/fancy-action-tracker-factory.service';

//types/ interfaces
type PathOrRefOrObservable = string | firebase.database.Reference | Observable<any>;
function isRef(x: RefOrObservable): x is firebase.database.Reference {
	return (<any>x).ref !== undefined;
}
function isObservable(x: RefOrObservable): x is Observable<any> {
	return (<any>x).subscribe !== undefined;
}

export class FancyFirebaseObject<FancyModel> {
	private shutdownSubject = new Subject<void>();

	public listRef: AngularFireObject<FancyModel>;

	protected _firebaseEvent$: Observable<any>; //oh screw you typescript

	/** @deprecated Direct access of _internalObject is deprecated, prefer subscription or if necessary use syncObject getter */
	public _internalObject: FancyModel = null;

	/** Provides syncronous access to the current state of the object. Use only for "point access", in general prefer a subscription. */
	public get syncObject() {
		// tslint:disable-next-line: deprecation
		return this._internalObject;
	}

	/** TODO - move private somehow */
	public appEvent$: Subject<FancyAction>;
	protected supplementalUpdate$: Subject<any>;

	/** Subscribe to recieve an updated list of tickets events. */
	public object$: BehaviorSubject<FancyModel>;

	public actionTracker: IFancyActionTrackerObj;

	constructor(
		private angularLand: AngularLandConnection,
		path: RefOrObservable
		// private actionCtor: new (...args: any[]) => SomeOtherActionName,
	) {
		this.actionTracker = this.angularLand.fancyActionTrackerFactory.create();

		this.object$ = new BehaviorSubject(this._internalObject);
		this.appEvent$ = new Subject();
		const mappedAppEvents: Observable<any> = this.handleCustomEvents(this.appEvent$).pipe(
			map((action) => this.splitDataCustomEventCompat(action))
		);

		this.supplementalUpdate$ = new Subject();
		const mappedSupplementalEvents = this.handleSupplementalEvents(this.supplementalUpdate$);

		this._firebaseEvent$ = merge(
			this.setupInputObservable(path).pipe(
				takeUntil(this.shutdownSubject),
				map((data) => this.$$updated(data))
			),
			mappedSupplementalEvents,
			mappedAppEvents
		).pipe(observeOn(queueScheduler));

		this._firebaseEvent$.subscribe((data) => {
			this._internalObject = data;
			this.object$.next(this._internalObject);
		});
	}

	private setupInputObservable(path: RefOrObservable) {
		if (isRef(path)) {
			//it's actually valid to pass a ref, the typescript definition is dumb
			this.listRef = this.angularLand.angularFireDatabase.object(path);
			return this.listRef.snapshotChanges();
		} else if (isObservable(path)) {
			return path;
		} else {
			throw new Error('the hell you pass in here');
		}
	}

	// all the split data stuff was written for the list, which has an action concept
	// that was never ported to the object. Thus far nothing has _needed_ it. So this is
	// temp permanent thing that basically just strips out the action part
	private splitDataCustomEventCompat(action: FancyAction) {
		return action.payload;
	}

	/**
	 * Called by the list building code,
	 * @param  action the angularfire snapshot
	 * @return        The constructed child object
	 */
	// $$added(action: AngularFireAction<DatabaseSnapshot<any>>){
	// 	var obj = action.payload.val();
	// 	if(obj){ obj.$id = action.key; }
	// 	return obj;
	// }
	//
	public $$updated(child: AngularFireAction<DatabaseSnapshot<any>>): FancyModel {
		//some typescript silliness for the default version. Only technically valid if your FancyModel _is_ a DatabaseSnapshot.
		//would not be required if this was abstract.
		return child as unknown as FancyModel;
	}
	// $$removed(child:FancyModel){
	//
	// }

	//pass in a supplemental obserable to subscribe to, optionally wrapping it in an action
	public addSupplementalObservable(obs: Observable<any>) {
		obs.subscribe(this.supplementalUpdate$);
	}

	//override with the custom observable chain
	public handleCustomEvents(baseObservable): Observable<FancyAction<FancyModel>> {
		return baseObservable;
	}

	public handleSupplementalEvents(baseObservable): Observable<any> {
		return EMPTY;
	}

	public destroy() {
		this.shutdownSubject.next();
		this.appEvent$.complete();
		this.object$.complete();
		this.actionTracker.actionTracker$.destroy(this.actionTracker.unsubscriber);
	}
}
